/**
 * Menu drawer on mobile/tablet: header drops in, page stays exactly where it
 * is, drawer slides in from the left; on close the drawer slides back and the
 * header lifts away again. Works for BOTH ways of opening it: the header
 * hamburger and the mobile bottom bar's Menu button.
 *
 * Why this is needed: on mobile the header is not reliably sticky (see the
 * overflow-x rule in assets/mobile-bottom-nav.css), so when the page is
 * scrolled down the header, and the drawer that hangs off it, are somewhere
 * up at the top of the page, off-screen. The theme then focuses the hamburger
 * inside the header and the browser scrolls the whole page up to reach it.
 *
 * What this does instead, while the menu is open (class spoilt-menu-open,
 * styled in assets/mobile-bottom-nav.css):
 *  - the header is pinned to the top of the screen, and drops in from above
 *    if it had scrolled out of view;
 *  - the page is frozen by fixing <body> at the current scroll offset, so
 *    nothing can scroll and there is nothing for the browser to jump to;
 *  - on close, once the drawer has slid away, the header lifts out again and
 *    the scroll position is put back before the browser paints.
 *
 * Everything is looked up at click time because the theme's own drawer script
 * loads independently of this one.
 */
(function () {
  var root = document.documentElement;
  var LOCK_CLASS = 'spoilt-menu-open';
  var DROP_CLASS = 'spoilt-header-dropping';
  var LIFT_CLASS = 'spoilt-header-lifting';

  var savedScrollY = 0;
  var headerWasOutOfView = false;
  var releasing = false;
  var swallowScroll = false;

  function getDetails() {
    return document.getElementById('Details-menu-drawer-container');
  }

  function getSummary() {
    var details = getDetails();
    return details ? details.querySelector('summary') : null;
  }

  function isDrawerOpen() {
    var details = getDetails();
    return !!details && details.hasAttribute('open');
  }

  function getSection() {
    return document.querySelector('.section-header');
  }

  function getHeader() {
    return document.querySelector('.section-header .header-wrapper');
  }

  function currentScrollY() {
    return window.pageYOffset || document.documentElement.scrollTop || 0;
  }

  // The drawer is only a full-screen overlay below 990px; leave desktop alone.
  function shouldLock() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  function isHeaderInView(header) {
    var rect = header.getBoundingClientRect();
    return rect.top >= -1 && rect.bottom > 1;
  }

  // Runs the callback when the element's CSS animation ends (with a timeout as
  // a safety net so the sequence can never get stuck).
  function afterAnimation(el, callback) {
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      if (el) el.removeEventListener('animationend', finish);
      callback();
    }
    if (el) el.addEventListener('animationend', finish);
    window.setTimeout(finish, 450);
  }

  // While the page is held in place the browser may report scroll events (the
  // document shrinks when <body> is fixed). The sticky-header script would
  // read those as the visitor scrolling and hide/show the header, so keep them
  // away from it. This capture listener runs before the theme's listener.
  window.addEventListener(
    'scroll',
    function (e) {
      if (swallowScroll) e.stopImmediatePropagation();
    },
    true
  );

  // CSS holds the page still; older iOS Safari can still rubber-band under a
  // touch, so also swallow touch moves that start outside the drawer itself.
  function blockPageScroll(e) {
    var drawer = document.getElementById('menu-drawer');
    if (drawer && drawer.contains(e.target)) return;
    e.preventDefault();
  }

  function lockPage(dropHeader) {
    if (root.classList.contains(LOCK_CLASS)) return;

    var section = getSection();
    savedScrollY = currentScrollY();
    headerWasOutOfView = !!dropHeader;
    swallowScroll = true;

    // Taking the header out of the flow would shrink the page and shift
    // everything up; keep its space reserved.
    if (section) section.style.minHeight = section.offsetHeight + 'px';
    root.style.setProperty('--spoilt-scroll-y', savedScrollY + 'px');

    root.classList.add(LOCK_CLASS);
    if (dropHeader) root.classList.add(DROP_CLASS);
    document.addEventListener('touchmove', blockPageScroll, { passive: false });
  }

  function releasePage() {
    var section = getSection();

    root.classList.remove(LOCK_CLASS, DROP_CLASS, LIFT_CLASS);
    if (section) section.style.minHeight = '';
    root.style.removeProperty('--spoilt-scroll-y');
    document.removeEventListener('touchmove', blockPageScroll, { passive: false });

    // Put the page back where it was before the browser paints, so nothing
    // visibly moves. scroll-behavior is switched off for this one jump in case
    // the theme animates scrolling.
    var previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, savedScrollY);
    root.style.scrollBehavior = previousBehavior;

    headerWasOutOfView = false;
    releasing = false;

    // Scroll events caused by that restore arrive on the next frame.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        swallowScroll = false;
      });
    });
  }

  function unlockPage() {
    if (!root.classList.contains(LOCK_CLASS) || releasing) return;
    releasing = true;

    if (headerWasOutOfView) {
      // Drawer has already slid away; now the header lifts out of sight.
      root.classList.remove(DROP_CLASS);
      root.classList.add(LIFT_CLASS);
      afterAnimation(getHeader(), releasePage);
    } else {
      releasePage();
    }
  }

  // If the drawer didn't open for any reason, never leave the page frozen.
  function unlockIfNotOpen() {
    window.setTimeout(function () {
      if (!isDrawerOpen()) unlockPage();
    }, 120);
  }

  // Header hamburger (and the real click that the Menu button fires below):
  // runs before the theme's own handler. The header is on screen here, so no
  // drop is needed; the page just gets held in place.
  document.addEventListener(
    'click',
    function (e) {
      var summary = getSummary();
      if (!summary || !summary.contains(e.target)) return;
      if (isDrawerOpen()) return; // this click closes it
      if (!shouldLock()) return;

      lockPage(false);
      unlockIfNotOpen();
    },
    true
  );

  // However the drawer ends up closed (X icon, backdrop, Escape key, the
  // bottom bar button), unlock once it has finished closing.
  var details = getDetails();
  if (details) {
    details.addEventListener('toggle', function () {
      if (!details.hasAttribute('open')) unlockPage();
    });
  }

  // Tapping a menu link navigates away with the drawer still open. Some
  // browsers keep a snapshot of the page for the Back button, which would bring
  // it back frozen with the menu open. Close everything on the way out.
  window.addEventListener('pagehide', function () {
    var openDetails = getDetails();
    if (openDetails && openDetails.hasAttribute('open')) {
      openDetails.classList.remove('menu-opening');
      openDetails.removeAttribute('open');
      document.body.classList.remove('overflow-hidden-tablet', 'overflow-hidden-desktop');
      var section = getSection();
      if (section) section.classList.remove('menu-open');
      var summary = getSummary();
      if (summary) summary.setAttribute('aria-expanded', 'false');
    }
    if (root.classList.contains(LOCK_CLASS)) {
      releasing = false;
      releasePage();
    }
  });

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      root.classList.remove(LOCK_CLASS, DROP_CLASS, LIFT_CLASS);
      swallowScroll = false;
    }
  });

  // Bottom bar Menu button.
  var menuBtn = document.querySelector('[data-mobile-menu-trigger]');
  if (!menuBtn) return;

  if (!details) {
    // No menu configured: a dead button is worse than no button.
    menuBtn.style.display = 'none';
    return;
  }

  function syncState() {
    var open = isDrawerOpen();
    menuBtn.classList.toggle('is-active', open);
    menuBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  details.addEventListener('toggle', syncState);
  syncState();

  // Open: if the header is already on screen, just open the drawer. If it has
  // scrolled away, drop it in first, then open the drawer once it has landed.
  function openFromBar(summary) {
    var header = getHeader();

    if (!shouldLock() || !header) {
      summary.click();
      return;
    }

    if (isHeaderInView(header)) {
      summary.click();
      return;
    }

    lockPage(true);
    afterAnimation(header, function () {
      root.classList.remove(DROP_CLASS);
      summary.click();
      unlockIfNotOpen();
    });
  }

  // Remember whether the drawer was open at the moment of the press, so a tap
  // meant to close it can never close it and then re-open it.
  var wasOpenOnPress = false;
  menuBtn.addEventListener('pointerdown', function () {
    wasOpenOnPress = isDrawerOpen();
  });
  menuBtn.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') wasOpenOnPress = isDrawerOpen();
  });

  menuBtn.addEventListener('click', function (e) {
    e.preventDefault();

    var summary = getSummary();
    if (!summary) return;

    if (wasOpenOnPress || isDrawerOpen()) {
      // Closing.
      wasOpenOnPress = false;
      if (isDrawerOpen()) summary.click();
      return;
    }

    // Header is still dropping in, or the page is mid-release: ignore.
    if (root.classList.contains(LOCK_CLASS)) return;

    openFromBar(summary);
  });
})();